import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ONBOARDING_TOTAL_STEPS,
  type UseOnboardingNavigation,
} from "../types";
import {
  normalizeHabitualStoreId,
  normalizeMonthlySpend,
  normalizePurchaseFrequency,
  normalizeStoreIds,
  normalizeSubscriptionIds,
  readOnboardingState,
  writeOnboardingState,
  type OnboardingState,
} from "../types/onboarding-state";

/**
 * Clamps a raw step value to the inclusive [1, ONBOARDING_TOTAL_STEPS] range.
 */
const clampStep = (raw: number): number => {
  if (Number.isNaN(raw)) {
    return 1;
  }
  if (raw < 1) {
    return 1;
  }
  if (raw > ONBOARDING_TOTAL_STEPS) {
    return ONBOARDING_TOTAL_STEPS;
  }
  return raw;
};

/**
 * Default onboarding state used when no persisted payload exists.
 *
 * Step starts at 1; city / location fields are intentionally `null` so the
 * onboarding flow can ask the user for a selection before persisting any
 * location data. `purchase_frequency` is also `null` so the wizard can
 * require a fresh selection on step 4 before persisting it. `monthly_spend`
 * follows the same pattern for step 5.
 */
const EMPTY_ONBOARDING_STATE: OnboardingState = {
  currentStep: 1,
  city_id: null,
  city_name: null,
  location_id: null,
  location_name: null,
  isFallback: false,
  purchase_frequency: null,
  monthly_spend: null,
  store_ids: [],
  subscription_ids: [],
  habitual_store_id: null,
};

/**
 * Normalizes a payload read from localStorage into a fully-shaped
 * `OnboardingState`. Missing or unexpected fields fall back to the empty
 * defaults; `currentStep` is clamped via `clampStep` so a tampered payload
 * cannot push the hook outside the legal range. `purchase_frequency`
 * flows through `normalizePurchaseFrequency` so an unknown enum value
 * stored by a tampered payload collapses to `null` instead of leaking
 * into the in-memory state. `monthly_spend` flows through
 * `normalizeMonthlySpend` for the same defense-in-depth reason.
 */
const hydrateOnboardingState = (
  persisted: Partial<OnboardingState> | null,
): OnboardingState => {
  if (!persisted) {
    return { ...EMPTY_ONBOARDING_STATE };
  }
  return {
    currentStep: clampStep(
      typeof persisted.currentStep === "number" ? persisted.currentStep : 1,
    ),
    city_id:
      typeof persisted.city_id === "number" ? persisted.city_id : null,
    city_name:
      typeof persisted.city_name === "string" ? persisted.city_name : null,
    location_id:
      typeof persisted.location_id === "number"
        ? persisted.location_id
        : null,
    location_name:
      typeof persisted.location_name === "string"
        ? persisted.location_name
        : null,
    isFallback: persisted.isFallback === true,
    purchase_frequency: normalizePurchaseFrequency(
      persisted.purchase_frequency,
    ),
    monthly_spend: normalizeMonthlySpend(persisted.monthly_spend),
    store_ids: normalizeStoreIds(persisted.store_ids),
    subscription_ids: normalizeSubscriptionIds(persisted.subscription_ids),
    habitual_store_id: normalizeHabitualStoreId(
      persisted.habitual_store_id,
    ),
  };
};

/**
 * Public contract exposed by the navigation hook.
 *
 * Slice 2 extends the slice-1 surface with `onboardingData`: a single
 * source of truth that downstream consumers (`StepThree`, the upcoming
 * `OnboardingPage` draft-state) can read without touching `localStorage`
 * directly.
 *
 * Slice 3 adds `setOnboardingData` so the page can atomically persist a
 * step-2 selection (city / sector / fallback) without losing the new
 * fields on the subsequent `next()` transition. The setter also clamps
 * `currentStep` via `clampStep` so the writer invariants from slice 2
 * are preserved.
 */
export interface UseOnboardingNavigationState
  extends UseOnboardingNavigation {
  onboardingData: OnboardingState;
  setOnboardingData: (next: OnboardingState) => void;
}

/**
 * Hook that owns onboarding wizard navigation state.
 *
 * Exposes `currentStep`, `totalSteps`, `next` / `prev` / `setStep` /
 * `complete`, and the full `onboardingData` payload. State is mirrored to
 * `localStorage["carriapp:onboarding"]` so re-mounting the wizard resumes
 * at the persisted step. `complete` navigates to `/store` (the
 * post-onboarding redirect target defined in the spec).
 */
export const useOnboardingNavigation = (): UseOnboardingNavigationState => {
  const [onboardingData, setOnboardingDataState] = useState<OnboardingState>(() =>
    hydrateOnboardingState(readOnboardingState()),
  );
  const navigate = useNavigate();

  /**
   * Internal helper that owns the "transition to a new step and persist"
   * pattern shared by `next`, `prev`, and `setStep`.
   *
   * `computeNextStep` receives the current step and returns the desired
   * (unclamped) step. The result is always clamped via `clampStep` before
   * being committed to state and persisted to localStorage, so every
   * entry point honours the same invariants.
   */
  const transitionToStep = useCallback(
    (computeNextStep: (currentStep: number) => number) => {
      setOnboardingDataState((prev) => {
        const latestDraft = hydrateOnboardingState(readOnboardingState());
        const base = latestDraft.currentStep === prev.currentStep ? latestDraft : prev;
        const clamped = clampStep(computeNextStep(prev.currentStep));
        const nextState: OnboardingState = { ...base, currentStep: clamped };
        writeOnboardingState(nextState);
        return nextState;
      });
    },
    [],
  );

  const next = useCallback(
    () => transitionToStep((step) => step + 1),
    [transitionToStep],
  );

  const prev = useCallback(
    () => transitionToStep((step) => step - 1),
    [transitionToStep],
  );

  const setStep = useCallback(
    (step: number) => transitionToStep(() => step),
    [transitionToStep],
  );

  const complete = useCallback(() => {
    navigate("/store");
  }, [navigate]);

  /**
   * Atomically replaces the in-memory onboarding payload and mirrors it
   * to localStorage. Used by the page when stepping from step 2 to 3 so
   * the city / sector fields are committed before the next transition.
   *
   * `currentStep` is clamped via `clampStep` so a tampered payload
   * cannot push the wizard outside the legal range.
   */
  const setOnboardingData = useCallback((next: OnboardingState) => {
    setOnboardingDataState((prev) => {
      const clamped: OnboardingState = {
        ...prev,
        ...next,
        currentStep: clampStep(next.currentStep),
      };
      writeOnboardingState(clamped);
      return clamped;
    });
  }, []);

  return useMemo(
    () => ({
      currentStep: onboardingData.currentStep,
      totalSteps: ONBOARDING_TOTAL_STEPS,
      onboardingData,
      next,
      prev,
      complete,
      setStep,
      setOnboardingData,
    }),
    [onboardingData, next, prev, complete, setStep, setOnboardingData],
  );
};
